/**
 * El prompt de instalación: cuándo toca y cuándo no (ADR-015).
 *
 * `docs/03` §6.1 pone una regla dura que este módulo hace cumplir:
 * **`beforeinstallprompt` se suprime siempre.** El navegador querría
 * mostrar su propia barra de instalación cuando le parezca; aquí se le
 * quita esa decisión y el prompt nativo solo se dispara desde nuestro
 * afordance, en el momento que ADR-015 autoriza.
 *
 * Ese momento es **justo después de agregar el primer lugar**, con un
 * respaldo único al tercer día distinto de uso. El momento original —el
 * ritual de la primera Colección— no existe todavía, y su copy prometía
 * que el mapa funciona sin señal, que ADR-006 retiró.
 *
 * Todo el estado vive en `localStorage` y no en el servidor a propósito:
 * la instalación es del **dispositivo**, no de la cuenta. La misma persona
 * en su teléfono y en su laptop tiene dos respuestas distintas, y
 * guardarlo en el perfil haría que instalar en uno silenciara el pedido en
 * el otro.
 */

export const OFRECIDO_KEY = 'gu.install_ofrecido';
export const RESPALDO_KEY = 'gu.install_respaldo';
export const DIAS_KEY = 'gu.dias_de_uso';

/** `docs/03` §6.2: el tercer día distinto es evidencia de hábito real. */
const DIAS_PARA_RESPALDO = 3;

/* ─────────────────────────── Almacenamiento ─────────────────────────── */

/*
 * Safari en modo privado llegó a lanzar al escribir en `localStorage`. Nada
 * de lo que hay aquí justifica dejar la app sin arrancar, así que todo
 * falla hacia "no lo sé" y sigue.
 */

function leer(clave: string): string | null {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: string): void {
  try {
    localStorage.setItem(clave, valor);
  } catch {
    /* sin persistencia: se volverá a ofrecer, y es el fallo correcto */
  }
}

/* ─────────────────────────── ¿Ya está puesta? ─────────────────────────── */

/**
 * ¿La app corre ya instalada?
 *
 * Dos comprobaciones porque hay dos mecanismos. El estándar es
 * `display-mode: standalone`; Safari en iOS **no lo implementa** y expone
 * en su lugar `navigator.standalone`. Sin la segunda rama, en iOS
 * instalado seguiríamos ofreciendo instalar.
 */
export function yaInstalada(): boolean {
  const iosStandalone = (navigator as { standalone?: boolean }).standalone;
  if (iosStandalone === true) return true;

  try {
    return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  } catch {
    return false;
  }
}

/* ─────────────────────────────── iOS ─────────────────────────────── */

/**
 * ¿Es iOS? Ahí no existe `beforeinstallprompt` y hay que dar
 * instrucciones manuales en vez de un botón que no haría nada.
 *
 * El iPad moderno obliga a la segunda comprobación: desde iPadOS 13 su
 * user-agent dice `Macintosh` y es indistinguible de un Mac por la cadena.
 * Lo que sí los separa es que un Mac no tiene puntos táctiles.
 */
export function esIOS(ua: string = navigator.userAgent): boolean {
  if (/iPhone|iPod/.test(ua)) return true;
  if (/iPad/.test(ua)) return true;

  const esMacintosh = /Macintosh/.test(ua);
  const tocable = (navigator.maxTouchPoints ?? 0) > 1;
  return esMacintosh && tocable;
}

/* ────────────────────────── Días distintos ────────────────────────── */

/**
 * Registra que la app se usó hoy y devuelve cuántos días DISTINTOS lleva.
 *
 * Días, no aperturas: abrir la app diez veces un martes es evidencia de
 * una sesión, no de hábito, y la regla del respaldo pide lo segundo.
 *
 * La lista se recorta a `DIAS_PARA_RESPALDO`: guardar un año de fechas
 * para una regla que se resuelve en el día 3 es acumular basura en el
 * almacenamiento de alguien.
 *
 * La fecha entra por parámetro para que sea comprobable sin congelar el
 * reloj — el llamador pasa `new Date().toISOString().slice(0, 10)`.
 */
function leerDias(): string[] {
  try {
    const crudo = leer(DIAS_KEY);
    if (!crudo) return [];

    const parsed: unknown = JSON.parse(crudo);
    // Datos corruptos —una versión anterior, otra app en el dominio— se
    // descartan en silencio: reiniciar el contador es infinitamente mejor
    // que romper el arranque.
    return Array.isArray(parsed) ? parsed.filter((d) => typeof d === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Cuántos días distintos lleva, sin registrar uno nuevo.
 *
 * Lo usa la pantalla que decide si toca ofrecer: registrar desde ahí
 * contaría el día dos veces —`App` ya lo hizo al arrancar— y, peor,
 * apuntaría un día de uso a quien solo abrió "tus lugares".
 */
export function diasDeUso(): number {
  return leerDias().length;
}

export function registrarDiaDeUso(hoy: string): number {
  let dias = leerDias();

  if (!dias.includes(hoy)) dias.push(hoy);
  if (dias.length > DIAS_PARA_RESPALDO) dias = dias.slice(-DIAS_PARA_RESPALDO);

  escribir(DIAS_KEY, JSON.stringify(dias));
  return dias.length;
}

/* ────────────────────────── La decisión ────────────────────────── */

export type MomentoDeInstalacion = 'principal' | 'respaldo';

export interface ContextoDeInstalacion {
  /** Lugares que esta persona ha agregado. El momento principal es ≥ 1. */
  lugaresPropios: number;
  /** Días distintos de uso, de `registrarDiaDeUso`. */
  diasDeUso: number;
}

/**
 * ¿Toca ofrecer la instalación, y en qué momento?
 *
 * Devuelve `null` la inmensa mayoría de las veces, y eso es lo correcto:
 * en toda la vida de un dispositivo esto dice que sí como mucho dos veces.
 */
export function debeOfrecer(ctx: ContextoDeInstalacion): MomentoDeInstalacion | null {
  if (yaInstalada()) return null;

  const ofrecido = leer(OFRECIDO_KEY) !== null;

  // El principal: acaba de crear algo suyo (ADR-015 §1).
  if (!ofrecido) {
    return ctx.lugaresPropios >= 1 ? 'principal' : null;
  }

  // El respaldo, uno solo. Sin la marca, cada apertura a partir del tercer
  // día volvería a pedirlo y el momento se convertiría en acoso.
  const respaldoUsado = leer(RESPALDO_KEY) !== null;
  if (respaldoUsado) return null;

  return ctx.diasDeUso >= DIAS_PARA_RESPALDO ? 'respaldo' : null;
}

export function marcarOfrecido(): void {
  escribir(OFRECIDO_KEY, '1');
}

export function marcarRespaldo(): void {
  escribir(RESPALDO_KEY, '1');
}
